import type { AustralianState, StateStampDutyConfig } from "../_types/calculator.types";

/**
 * Australian Stamp Duty Rates 2026
 *
 * DISCLAIMER: These rates are indicative only and may change.
 * Always verify with official state revenue office sources.
 *
 * Sources:
 * - NSW: revenue.nsw.gov.au
 * - VIC: sro.vic.gov.au
 * - QLD: qro.qld.gov.au
 * - WA: wa.gov.au/organisation/department-of-finance
 * - SA: revenuesa.sa.gov.au
 * - TAS: treasury.tas.gov.au
 * - ACT: revenue.act.gov.au
 * - NT: treasury.nt.gov.au
 */

export const stampDutyConfig: Record<AustralianState, StateStampDutyConfig> = {
  /**
   * NEW SOUTH WALES
   */
  NSW: {
    rates: [
      { min: 0, max: 16000, base: 0, rate: 0.0125 },
      { min: 16001, max: 35000, base: 200, rate: 0.015 },
      { min: 35001, max: 93000, base: 485, rate: 0.0175 },
      { min: 93001, max: 351000, base: 1500, rate: 0.035 },
      { min: 351001, max: 1168000, base: 10530, rate: 0.045 },
      { min: 1168001, max: 3505000, base: 47295, rate: 0.055 },
      { min: 3505001, max: null, base: 175830, rate: 0.07 },
    ],
    foreignBuyerSurcharge: 0.08,
    fhbConcession: {
      exemptionThreshold: 800000, // Full exemption up to $800k for new homes
      concessionThreshold: 1000000, // Partial concession up to $1M
    },
    fhog: {
      amount: 10000,
      newHomeOnly: true,
      maxPropertyValue: 600000,
    },
    mortgageRegistrationFee: 165,
    transferFeeBase: 165,
    transferFeePerThousand: 0,
    pensionerConcession: {
      maxPropertyValue: 600000,
      concessionPercent: 0.5,
    },
  },

  /**
   * VICTORIA
   */
  VIC: {
    rates: [
      { min: 0, max: 25000, base: 0, rate: 0.014 },
      { min: 25001, max: 130000, base: 350, rate: 0.024 },
      { min: 130001, max: 960000, base: 2870, rate: 0.06 },
      { min: 960001, max: 2000000, base: 52670, rate: 0.055 },
      { min: 2000001, max: null, base: 110000, rate: 0.065 },
    ],
    foreignBuyerSurcharge: 0.08,
    fhbConcession: {
      exemptionThreshold: 600000,
      concessionThreshold: 750000,
    },
    fhog: {
      amount: 10000,
      newHomeOnly: true,
      maxPropertyValue: 750000,
      regionalBonus: 10000, // Extra $10k for regional areas
    },
    mortgageRegistrationFee: 125,
    transferFeeBase: 100,
    transferFeePerThousand: 2.46,
    pensionerConcession: {
      maxPropertyValue: 750000,
      concessionPercent: 0.5,
    },
  },

  /**
   * QUEENSLAND
   */
  QLD: {
    rates: [
      { min: 0, max: 5000, base: 0, rate: 0 },
      { min: 5001, max: 75000, base: 0, rate: 0.015 },
      { min: 75001, max: 540000, base: 1050, rate: 0.035 },
      { min: 540001, max: 1000000, base: 17325, rate: 0.045 },
      { min: 1000001, max: null, base: 38025, rate: 0.0575 },
    ],
    foreignBuyerSurcharge: 0.07,
    fhbConcession: {
      exemptionThreshold: 700000, // Full concession for homes up to $700k
      concessionThreshold: 800000, // Partial up to $800k
    },
    fhog: {
      amount: 30000, // QLD has generous FHOG for new homes
      newHomeOnly: true,
      maxPropertyValue: 750000,
    },
    mortgageRegistrationFee: 197,
    transferFeeBase: 197,
    transferFeePerThousand: 0,
    pensionerConcession: {
      maxPropertyValue: 500000,
      concessionPercent: 1.0, // Full exemption
    },
  },

  /**
   * WESTERN AUSTRALIA
   */
  WA: {
    rates: [
      { min: 0, max: 120000, base: 0, rate: 0.019 },
      { min: 120001, max: 150000, base: 2280, rate: 0.0285 },
      { min: 150001, max: 360000, base: 3135, rate: 0.038 },
      { min: 360001, max: 725000, base: 11115, rate: 0.0475 },
      { min: 725001, max: null, base: 28453, rate: 0.0515 },
    ],
    foreignBuyerSurcharge: 0.07,
    fhbConcession: {
      exemptionThreshold: 430000,
      concessionThreshold: 530000,
    },
    fhog: {
      amount: 10000,
      newHomeOnly: true,
      maxPropertyValue: 750000,
    },
    mortgageRegistrationFee: 200,
    transferFeeBase: 200,
    transferFeePerThousand: 0,
  },

  /**
   * SOUTH AUSTRALIA
   */
  SA: {
    rates: [
      { min: 0, max: 12000, base: 0, rate: 0.01 },
      { min: 12001, max: 30000, base: 120, rate: 0.02 },
      { min: 30001, max: 50000, base: 480, rate: 0.03 },
      { min: 50001, max: 100000, base: 1080, rate: 0.035 },
      { min: 100001, max: 200000, base: 2830, rate: 0.04 },
      { min: 200001, max: 250000, base: 6830, rate: 0.0425 },
      { min: 250001, max: 300000, base: 8955, rate: 0.0475 },
      { min: 300001, max: 500000, base: 11330, rate: 0.05 },
      { min: 500001, max: null, base: 21330, rate: 0.055 },
    ],
    foreignBuyerSurcharge: 0.07,
    fhbConcession: {
      exemptionThreshold: 650000, // No stamp duty for FHB up to $650k
      concessionThreshold: 700000,
    },
    fhog: {
      amount: 15000,
      newHomeOnly: true,
      maxPropertyValue: 650000,
    },
    mortgageRegistrationFee: 188,
    transferFeeBase: 188,
    transferFeePerThousand: 0,
    pensionerConcession: {
      maxPropertyValue: 500000,
      concessionPercent: 0.5,
    },
  },

  /**
   * TASMANIA
   */
  TAS: {
    rates: [
      { min: 0, max: 3000, base: 50, rate: 0 },
      { min: 3001, max: 25000, base: 50, rate: 0.0175 },
      { min: 25001, max: 75000, base: 435, rate: 0.0225 },
      { min: 75001, max: 200000, base: 1560, rate: 0.035 },
      { min: 200001, max: 375000, base: 5935, rate: 0.04 },
      { min: 375001, max: 725000, base: 12935, rate: 0.0425 },
      { min: 725001, max: null, base: 27810, rate: 0.045 },
    ],
    foreignBuyerSurcharge: 0.08,
    fhbConcession: {
      exemptionThreshold: 750000, // 50% discount for FHB
      concessionThreshold: 750000,
      maxConcession: 7000,
    },
    fhog: {
      amount: 30000, // TAS has $30k FHOG for new builds
      newHomeOnly: true,
      maxPropertyValue: 600000,
    },
    mortgageRegistrationFee: 154,
    transferFeeBase: 253,
    transferFeePerThousand: 0,
    pensionerConcession: {
      maxPropertyValue: 500000,
      concessionPercent: 0.5,
    },
  },

  /**
   * AUSTRALIAN CAPITAL TERRITORY
   * Note: ACT is phasing out stamp duty for owner-occupiers
   */
  ACT: {
    rates: [
      { min: 0, max: 260000, base: 0, rate: 0.006 },
      { min: 260001, max: 300000, base: 1560, rate: 0.023 },
      { min: 300001, max: 500000, base: 2480, rate: 0.0405 },
      { min: 500001, max: 750000, base: 10580, rate: 0.0455 },
      { min: 750001, max: 1000000, base: 21955, rate: 0.0505 },
      { min: 1000001, max: 1455000, base: 34580, rate: 0.056 },
      { min: 1455001, max: null, base: 60060, rate: 0.0575 },
    ],
    foreignBuyerSurcharge: 0.0, // ACT uses land tax surcharge instead
    fhbConcession: {
      exemptionThreshold: 1020000, // No duty up to $1,020,000 from 1 July 2025
      concessionThreshold: 1455000,
    },
    fhog: {
      amount: 7500, // ACT has Home Buyer Concession Scheme instead
      newHomeOnly: true,
      maxPropertyValue: 750000,
    },
    mortgageRegistrationFee: 156,
    transferFeeBase: 491,
    transferFeePerThousand: 0,
    pensionerConcession: {
      maxPropertyValue: 850000,
      concessionPercent: 0.25,
    },
  },

  /**
   * NORTHERN TERRITORY
   */
  NT: {
    rates: [
      { min: 0, max: 525000, base: 0, rate: 0.0 }, // NT has stamp duty relief
      { min: 525001, max: 3000000, base: 0, rate: 0.0495 },
      { min: 3000001, max: 5000000, base: 122513, rate: 0.0575 },
      { min: 5000001, max: null, base: 237513, rate: 0.0595 },
    ],
    foreignBuyerSurcharge: 0.0,
    fhbConcession: {
      exemptionThreshold: 650000,
      concessionThreshold: 650000,
    },
    fhog: {
      amount: 50000, // NT HomeGrown Territory Grant - $50k new builds, extended to Sept 2027
      newHomeOnly: true,
      maxPropertyValue: 750000,
    },
    mortgageRegistrationFee: 156,
    transferFeeBase: 156,
    transferFeePerThousand: 0,
  },
};

/**
 * Get state stamp duty configuration
 */
export function getStateConfig(state: AustralianState): StateStampDutyConfig {
  return stampDutyConfig[state];
}
